/**
 * I18nService — Runtime internationalization.
 *
 * Loads JSON locale files, switches at runtime, notifies listeners.
 * Type-safe via the TranslationKey union in ./types.ts (auto-generated).
 *
 * See: .agent/rules/edutool-i18n.md
 */

import type { Locale } from '@/bridge/bridge-types';
import type { TranslationKey } from './types';
import { I18nError } from '@/core/errors';

export type TranslationValues = Record<string, string | number>;

type LocaleData = Record<string, string>;

const LOCALE_MODULES = import.meta.glob('./locales/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, LocaleData>;

export class I18nService {
  private currentLocale: Locale = 'it';
  private readonly fallbackLocale: Locale = 'it';
  private readonly cache: Partial<Record<Locale, LocaleData>> = {};
  private readonly listeners = new Set<(locale: Locale) => void>();
  private readonly availableLocales: Locale[];

  constructor(availableLocales: Locale[] = ['it', 'en']) {
    this.availableLocales = availableLocales;
  }

  async init(locale: Locale = 'it'): Promise<void> {
    await this.loadLocale(this.fallbackLocale);
    if (locale !== this.fallbackLocale) {
      await this.loadLocale(locale);
    }
    this.currentLocale = locale;
  }

  async setLocale(locale: Locale): Promise<void> {
    if (!this.availableLocales.includes(locale)) {
      throw new I18nError(`Locale not available: ${locale}`, { locale });
    }
    if (!this.cache[locale]) {
      await this.loadLocale(locale);
    }
    this.currentLocale = locale;
    document.documentElement.lang = locale;
    this.listeners.forEach((cb) => {
      try {
        cb(locale);
      } catch (err) {
        console.error('[I18nService] listener error:', err);
      }
    });
  }

  getLocale(): Locale {
    return this.currentLocale;
  }

  getAvailableLocales(): readonly Locale[] {
    return this.availableLocales;
  }

  /**
   * Subscribe to locale changes. Returns an unsubscribe function.
   */
  onChange(callback: (locale: Locale) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Translate a key. Falls back to the default locale if missing,
   * then to the key itself.
   */
  t(key: TranslationKey | string, values?: TranslationValues): string {
    const template =
      this.cache[this.currentLocale]?.[key] ??
      this.cache[this.fallbackLocale]?.[key] ??
      key;
    return this.interpolate(template, values);
  }

  /**
   * Checks if a key exists in the current locale (without falling back).
   */
  has(key: string): boolean {
    return this.cache[this.currentLocale]?.[key] !== undefined;
  }

  private interpolate(template: string, values?: TranslationValues): string {
    if (!values) return template;
    return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
      const value = values[name];
      return value !== undefined ? String(value) : match;
    });
  }

  private async loadLocale(locale: Locale): Promise<void> {
    const data = LOCALE_MODULES[`./locales/${locale}.json`];
    if (!data) {
      throw new I18nError(`Locale not found: ${locale}`, { locale });
    }
    this.cache[locale] = data;
  }
}
